import React, { useEffect, useMemo, useState } from 'react';
import { Variant } from '@Types/product/Variant';
import useSWR from 'swr';
import { UIProduct } from 'components/commercetools-ui/products/product-details';
import CheckmarkIcon from 'components/icons/checkmark';
import EditIcon from 'components/icons/editPencil';
import PickUpAtStoreIcon from 'components/icons/location-marker';
import ShipToAddressIcon from 'components/icons/shipping-truck';
import { useFormat } from 'helpers/hooks/useFormat';
import { isVeteranAccount } from 'helpers/utils/isVeteranAccount';
import { getDefaultShippingMethod } from 'helpers/utils/pdpShippingMethod';
import { fetchApiHub, revalidateOptions, useAccount } from 'frontastic';
import ChangeStore from '../../Modals/changeStore';
export interface ShippingOptionsProps {
  product: UIProduct;
  variant: Variant;
  handleAddToCart: any;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  shippingMethod: {
    isPickupInStore: any;
    isShipToAddress: any;
  };
  setShippingMethod: React.Dispatch<
    React.SetStateAction<{
      isPickupInStore: any;
      isShipToAddress: any;
    }>
  >;
  maxQuantity: number;
  showProductFrequency: boolean;
  isAddToCartDisabledForPUIS: boolean;
  isAddToCartDisabledForSTA: boolean;
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}
// TODO : will be managed dynamically in another ticket
function ShippingMethod() {
  return (
    <>
      <div className=" border-r-full rounded-tr-[3px] border-l-[35px] border-t-[35px] border-solid border-[#043C63] border-x-transparent border-b-transparent"></div>
      <div className="absolute top-1 right-1">
        <CheckmarkIcon />
      </div>
    </>
  );
}

function ShippingOptions({
  product,
  variant,
  handleAddToCart,
  quantity,
  setQuantity,
  shippingMethod,
  setShippingMethod,
  maxQuantity,
  showProductFrequency,
  isAddToCartDisabledForPUIS,
  isAddToCartDisabledForSTA,
}: ShippingOptionsProps) {
  const availableShippingMethods = {
    isPickupInStore: showProductFrequency ? false : variant?.isPickupInStore,
    isShipToAddress: variant?.isShipToAddress,
  };
  const isPickupInStore = variant?.isPickupInStore;
  const { account, listingStores } = useAccount();
  const isPUISShippingMethodAvailabe = availableShippingMethods?.isPickupInStore && !isAddToCartDisabledForPUIS;
  const isSTAShippingMethodAvailabe = !isAddToCartDisabledForSTA && availableShippingMethods?.isShipToAddress;
  const isStoreOverlayButtonDisabled = !availableShippingMethods?.isPickupInStore || isVeteranAccount(account);
  const [isShowStoreModal, setIsShowStoreModal] = useState(false);
  const [isEligibleToShip, setisEligibleToShip] = useState(false);
  const [isEligibleToShipLoader, setisEligibleToShipLoader] = useState(false);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const isItemNotAvailbleToSelectedStore = availableShippingMethods?.isPickupInStore && !isPUISShippingMethodAvailabe;
  const shippingOptionData = useMemo(() => {
    return {
      _url: product?._url,
      variant: variant,
      name: product?.name,
    };
  }, [product, variant]);

  const { data: customMsg } = useSWR(
    `/action/customObjects/getCustomMessages?messages=storeNotAvailableForBOPIS,notAvailableAtAnyLocation`,
    fetchApiHub,
    revalidateOptions,
  );

  useEffect(() => {
    setShippingMethod(getDefaultShippingMethod(variant));
  }, [account?.defaultStore?.key]);

  useEffect(() => {
    const fetchChannelList = async () => {
      setisEligibleToShipLoader(true);
      try {
        const channelList = await listingStores(variant?.sku);
        const hasEligibleStore = channelList.some((store) => store?.isShipToEligible && store?.isOnStock);
        setisEligibleToShip(hasEligibleStore);
      } catch (error) {
        console.error('Error fetching channel list:', error);
      } finally {
        setisEligibleToShipLoader(false); // Stop loading
      }
    };

    if (variant?.sku) {
      fetchChannelList();
    }
  }, [variant?.sku]);

  return (
    <>
      <section>
        <div className="flex justify-center gap-x-3.5 py-4 xl:mr-3">
          <label
            htmlFor="pickUpAtStore"
            role="radio"
            aria-checked={isPUISShippingMethodAvailabe && shippingMethod?.isPickupInStore}
            className={classNames(
              'relative min-h-[109px] w-full cursor-pointer rounded-[4px] border-[1px] border-solid bg-[#FFFFFF] p-4 md:max-w-[136px]',
              isPUISShippingMethodAvailabe && 'border-[#043C63]',
            )}
          >
            <div className="absolute right-0 top-0 h-[35px] w-[35px]">
              {isPUISShippingMethodAvailabe && shippingMethod?.isPickupInStore && <ShippingMethod />}
            </div>
            <div className="flex h-full items-center justify-center">
              <div className="">
                <div className="flex justify-center pb-2 text-[#333333] lg:pb-4">
                  <PickUpAtStoreIcon fill={`${isPUISShippingMethodAvailabe ? '#333333' : '#dddddd'}`} />
                  <input
                    disabled={!isPUISShippingMethodAvailabe}
                    type="radio"
                    id="pickUpAtStore"
                    name="shippingOption"
                    checked={isPUISShippingMethodAvailabe && shippingMethod?.isPickupInStore}
                    onChange={() =>
                      setShippingMethod((prevShippingMethod) => ({
                        ...prevShippingMethod,
                        isPickupInStore: true,
                        isShipToAddress: false,
                      }))
                    }
                    className="invisible h-0 w-0"
                    aria-labelledby="pickUpAtStoreLabel"
                  />
                </div>
                <div
                  id="pickUpAtStoreLabel"
                  className={`font-lato text-center text-[14px] font-normal leading-[17px] ${
                    isPUISShippingMethodAvailabe ? 'text-[#333333]' : 'text-[#dddddd]'
                  }`}
                >
                  <div className="hidden md:block">
                    <>
                      <p>{formatProductMessage({ id: 'pickUpAt', defaultMessage: 'Pick up at' })}</p>
                      <p>{formatProductMessage({ id: 'store', defaultMessage: 'store' })}</p>
                    </>
                  </div>
                  <div className="md:hidden">
                    <>{formatProductMessage({ id: 'pickUpAtStore', defaultMessage: 'Pick up at store' })}</>
                  </div>
                </div>
              </div>
            </div>
          </label>

          <label
            htmlFor="shipToAddress"
            role="radio"
            aria-checked={isSTAShippingMethodAvailabe && shippingMethod?.isShipToAddress}
            className={classNames(
              'relative min-h-[109px] w-full cursor-pointer rounded-[4px] border-[1px] border-solid bg-[#FFFFFF] p-4 md:max-w-[136px]',
              isSTAShippingMethodAvailabe && 'border-[#043C63]',
            )}
          >
            <div className="absolute right-0 top-0 h-[35px] w-[35px]">
              {isSTAShippingMethodAvailabe && shippingMethod?.isShipToAddress && <ShippingMethod />}
            </div>
            <div className="flex h-full items-center justify-center">
              <div className="">
                <div className="flex justify-center pb-2 text-[#043C63] lg:pb-4">
                  <ShipToAddressIcon fill={`${isSTAShippingMethodAvailabe ? '#043C63' : '#dddddd'}`} />
                  <input
                    disabled={!isSTAShippingMethodAvailabe}
                    type="radio"
                    id="shipToAddress"
                    name="shippingOption"
                    checked={shippingMethod?.isShipToAddress}
                    onChange={() =>
                      setShippingMethod((prevShippingMethod) => ({
                        ...prevShippingMethod,
                        isShipToAddress: true,
                        isPickupInStore: false,
                      }))
                    }
                    className="invisible h-0 w-0"
                    aria-labelledby="shipToAddressLabel"
                  />
                </div>
                <div
                  id="shipToAddressLabel"
                  className={`font-lato text-center text-[14px] font-normal leading-[17px] ${
                    isSTAShippingMethodAvailabe ? 'text-[#043C63]' : 'text-[#dddddd]'
                  }`}
                >
                  {!isSTAShippingMethodAvailabe ? (
                    <>
                      <div className="hidden md:block">
                        <>
                          <p>{formatProductMessage({ id: 'shipping', defaultMessage: 'Shipping' })}</p>
                          <p>{formatProductMessage({ id: 'notAvailable', defaultMessage: 'not available' })}</p>
                        </>
                      </div>
                      <div className="md:hidden">
                        <>
                          {formatProductMessage({
                            id: 'ShippingNotAvailable',
                            defaultMessage: 'Shipping not available',
                          })}
                        </>
                      </div>
                    </>
                  ) : (
                    <>{formatProductMessage({ id: 'shipToAddress', defaultMessage: 'Ship to Address' })}</>
                  )}
                </div>
              </div>
            </div>
          </label>
        </div>

        {isPickupInStore && account?.defaultStore?.key && (
          <div className={isItemNotAvailbleToSelectedStore ? '' : 'flex'}>
            <span>{account?.defaultStore?.name}</span>
            {isEligibleToShipLoader ? (
              <div role="status">
                <svg
                  aria-hidden="true"
                  className="ml-2 inline h-4 w-4 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">{`Loading...`}</span>
              </div>
            ) : isEligibleToShip ? (
              <span className="block text-red-900">
                {isItemNotAvailbleToSelectedStore && <>{customMsg?.storeNotAvailableForBOPIS}</>}
                <button type="button" disabled={isStoreOverlayButtonDisabled} onClick={() => setIsShowStoreModal(true)}>
                  <EditIcon
                    className="ml-2 cursor-pointer"
                    fill={`${!isStoreOverlayButtonDisabled ? '#043C63' : '#dddddd'}`}
                  />
                </button>
              </span>
            ) : (
              <div>
                <span className="text-[#268936]">{customMsg?.notAvailableAtAnyLocation}</span>
              </div>
            )}
          </div>
        )}
      </section>
      {isShowStoreModal && isPickupInStore && (
        <ChangeStore
          closeModal={() => setIsShowStoreModal(false)}
          data={shippingOptionData}
          isPDP={true}
          handleAddToCart={handleAddToCart}
          quantity={quantity}
          setQuantity={setQuantity}
          maxQuantity={maxQuantity}
          isItemNotAvailbleToSelectedStore={isItemNotAvailbleToSelectedStore}
        />
      )}
    </>
  );
}
export default ShippingOptions;
